import React, { FC } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import LinkIcon from '@mui/icons-material/Link';
import { fixUrl } from '../utils/formatUrl';
import type { LinkPreview } from '../types/common';

interface LinkPreviewCardProps {
  link: LinkPreview;
}

const LinkPreviewCard: FC<LinkPreviewCardProps> = ({ link }) => {
  const theme = useTheme();
  const domain = link.domain || (() => {
    try {
      return new URL(fixUrl(link.url)).hostname.replace(/^www\./, '');
    } catch {
      return link.url;
    }
  })();

  return (
    <Link
      href={fixUrl(link.url)}
      target="_blank"
      rel="noopener noreferrer"
      underline="none"
      sx={{
        display: 'block',
        border: `${theme.tokens.borders.widthThin} ${theme.tokens.borders.style} ${theme.tokens.colors.cardBorder}`,
        borderRadius: 'var(--sh-lg)',
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
        transition: `border-color ${theme.tokens.animations.fast} ${theme.tokens.animations.easing}`,
        '&:hover': { borderColor: theme.palette.primary.main },
      }}
    >
      {link.image && (
        <Box
          sx={{
            height: 140,
            backgroundImage: `url(${link.image})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
      )}
      <Box sx={{ p: 1.75 }}>
        <Typography
          sx={{
            fontFamily: theme.tokens.typography.monoFontFamily,
            fontSize: theme.tokens.typography.sizes.caption,
            color: theme.palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            mb: 0.5,
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <LinkIcon fontSize="inherit" />
          {domain}
          <OpenInNewIcon fontSize="inherit" />
        </Typography>
        {link.title && (
          <Typography
            sx={{
              fontWeight: theme.tokens.typography.fontWeightBold,
              color: theme.tokens.colors.textHeading,
              fontSize: theme.tokens.typography.sizes.body,
              mb: link.description ? 0.5 : 0,
            }}
          >
            {link.title}
          </Typography>
        )}
        {link.description && (
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.text.secondary,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {link.description}
          </Typography>
        )}
      </Box>
    </Link>
  );
};

export default LinkPreviewCard;
